/* =========================================================================
   TISMS design tokens — literal values from WEB_SCREENS_SPEC.md §1
   ========================================================================= */
import type { Grade, Severity } from '../data/tisms-api';

/* ---- 1.1 Brand / neutrals ---- */
export const colors = {
  brandGreen: '#0F5132', // primary buttons, headings, top bar, active nav accent
  deepGreen: '#123A28', // sidebar bg, dark panels, inspection area header
  darkestGreen: '#14352A', // dashboard hero gradient tail
  loginBg: '#0C2B1D', // login page base + mesh base
  gold: '#B8860B', // accent, gold buttons, progress fills, chevrons, area numbers
  goldLight: '#D8B75B', // kickers, campaign progress bar, sidebar group labels
  goldLighter: '#E4C878', // login chip text, inspection header pill text
  goldLighter2: '#E3C878',
  cream: '#F7F4EC', // page bg, light text on dark
  creamPanel: '#FBFAF4', // table row hover, soft cards, checklist unselected inset
  creamTab: '#F3F0E5', // reason pills, tree hover
  ink: '#1C2A26', // body text, dark toast bg
  muted: '#5A6460', // secondary text, table labels
  faint: '#8B9490', // tertiary text, meta
  borderWarm: '#D9D4C5', // default input/button borders
  borderWarm2: '#E9E5D6', // soft card borders
  rowDivider: '#F0EDE2', // table row top borders
  trackGreen: '#E9EFEB', // progress track, gauge track, info chips bg
  infoGreenText: '#14563A', // text on #E9EFEB chips

  // other semantic hues
  success: '#1B7F4B',
  warnAmber: '#9C7A16',
  warnAmber2: '#8A6A0E',
  orange: '#C05621',
  orange2: '#D26A2A',
  danger: '#B3261E',
  danger2: '#8E211B',
  danger3: '#8C1D18',
  blue: '#2A5D8F',
  blueBg: '#E4EDF7',
  purple: '#7A4FA3',
} as const;

/* ---- 1.1 Severity colours (sevColor) ---- */
export const severityColors: Record<Severity, string> = {
  low: '#5A8F3C',
  medium: '#D9A422',
  high: '#D26A2A',
  critical: '#B3261E',
};
export const sevColor = (s: Severity): string => severityColors[s] ?? colors.muted;

/* ---- 1.1 Grade ramp A–E (gradeColors → [bg, fg]) ---- */
export const gradeRamp: Record<Grade, [string, string]> = {
  A: ['#E3F1E7', '#1B7F4B'],
  B: ['#EDF3E0', '#5A8F3C'],
  C: ['#FAF1D8', '#9C7A16'],
  D: ['#FBE9DC', '#C05621'],
  E: ['#F9E2E0', '#B3261E'],
};
export const gradeColors = (g: string): [string, string] =>
  gradeRamp[g as Grade] ?? ['#EEE', '#555'];

/* ---- 1.1 Status pills (statusPill → [bg, fg]) ---- */
export const statusRamp: Record<string, [string, string]> = {
  Open: ['#F9E2E0', '#B3261E'],
  'In progress': ['#FAF1D8', '#9C7A16'],
  'Closure submitted': ['#E4EDF7', '#2A5D8F'],
  Closed: ['#E3F1E7', '#1B7F4B'],
  Rejected: ['#F1E4F6', '#7A4FA3'],
  Planned: ['#EDEBE0', '#5A6460'],
  'Data requested': ['#E4EDF7', '#2A5D8F'],
  Completed: ['#E3F1E7', '#1B7F4B'],
  'Report issued': ['#E3F1E7', '#0F5132'],
};
export const statusPill = (st: string): [string, string] =>
  statusRamp[st] ?? ['#EEE', '#555'];

/* ---- CRAR heatmap bands (healthy / watch / stressed) ---- */
export const heatColors = {
  healthy: { bg: '#DCEEE2', fg: '#14563A' },
  watch: { bg: '#F8ECC9', fg: '#8A6A0E' },
  stressed: { bg: '#F6D9D5', fg: '#8E211B' },
} as const;
export const crarBand = (crar: number): 'healthy' | 'watch' | 'stressed' =>
  crar >= 9 ? 'healthy' : crar >= 7 ? 'watch' : 'stressed';

/* ---- 1.2 Typography ---- */
export const fonts = {
  body: '"Segoe UI", Roboto, "Noto Sans Tamil", system-ui, sans-serif',
  serif: "'Source Serif 4', serif",
} as const;

export const fontSize = {
  pageTitle: 26,
  heroTitle: 30,
  loginBrand: 34,
  loginHeadline: 44,
  scoringBig: 52,
  gaugeNumber: 30,
  kpiValue: 30,
  cardHeading: 14,
  subheading: 13,
  body: 13,
  tableBody: 12.5,
  tableHeader: 11,
  meta: 11,
  micro: 10,
  kicker: 11.5,
} as const;

/* ---- 1.3 Radii, shadows, spacing ---- */
export const radii = {
  card: 12,
  smallCard: 10,
  button: 9,
  pill: 99,
  input: 8,
  modal: 16,
  orderDoc: 4,
  checkbox: 6,
  logo: 8,
} as const;

export const shadows = {
  card: '0 4px 18px rgba(28,42,38,.07)',
  kpi: '0 10px 30px rgba(28,42,38,.12)',
  modal: '0 30px 80px rgba(0,0,0,.4)',
  orderDoc: '0 30px 80px rgba(0,0,0,.45)',
  darkPanel: '0 6px 20px rgba(15,81,50,.25)',
  topBar: '0 2px 10px rgba(0,0,0,.18)',
  primaryBtn: '0 8px 20px rgba(15,81,50,.35)',
  goldBtn: '0 8px 20px rgba(184,134,11,.3)',
  drawer: '-20px 0 60px rgba(0,0,0,.3)',
  toast: '0 12px 32px rgba(0,0,0,.3)',
  loginCard: '0 30px 80px rgba(0,0,0,.45)',
} as const;

export const spacing = {
  cardPad: 20,
  cardPadDense: 16,
  pageX: 26,
  gridGap: 14,
  innerGap: 10,
} as const;

/* ---- 1.4 Gradients / mesh ---- */
export const gradients = {
  loginMesh:
    'radial-gradient(42% 55% at 22% 28%, rgba(31,110,68,.85), transparent 62%),' +
    'radial-gradient(38% 48% at 78% 24%, rgba(184,134,11,.38), transparent 60%),' +
    'radial-gradient(50% 60% at 62% 82%, rgba(16,74,45,.9), transparent 65%),' +
    'radial-gradient(30% 40% at 12% 85%, rgba(184,134,11,.22), transparent 60%),' +
    '#0C2B1D',
  dashboardHero: 'linear-gradient(160deg, #0F5132 0%, #123A28 70%, #14352A 100%)',
  campaignBanner: 'linear-gradient(120deg, #123A28, #0F5132)',
} as const;

/* ---- 1.5 Keyframes (injected once by App) ---- */
export const keyframesCss = `
@keyframes meshShift { 0%{transform:translate(0,0) scale(1)} 50%{transform:translate(-4%,3%) scale(1.08)} 100%{transform:translate(0,0) scale(1)} }
@keyframes floatA { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
@keyframes fadeUp { from{opacity:0; transform:translateY(14px)} to{opacity:1; transform:translateY(0)} }
@keyframes spinIn { from{opacity:0; transform:scale(.94)} to{opacity:1; transform:scale(1)} }
@keyframes pulseDot { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes spin { to{ transform:rotate(360deg) } }
@media (prefers-reduced-motion: reduce){ *,*::before,*::after{ animation:none!important; transition:none!important } }
`;

/* ---- Inspection status button colours (STATUSES) ---- */
export const STATUSES: { code: string; label: string; color: string }[] = [
  { code: 'C', label: 'Compliant', color: '#1B7F4B' },
  { code: 'Mi', label: 'Minor', color: '#9C7A16' },
  { code: 'Ma', label: 'Major', color: '#C05621' },
  { code: 'Cr', label: 'Critical', color: '#B3261E' },
  { code: 'Rp', label: 'Repeat', color: '#7A4FA3' },
  { code: 'F?', label: 'Fraud?', color: '#8C1D18' },
  { code: 'NA', label: 'N/A', color: '#5A6460' },
];
